import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { HTMLInputTypeAttribute } from 'react';
import React, { createElement } from 'react';
import type { SetOptional } from 'type-fest';
import type { OptionTypeBase } from '../Select';
import type { CheckRadioControlProps } from './CheckRadioControl';
import CheckRadioControl from './CheckRadioControl';
import * as styles from './FormField.module.scss';
import type { SelectControlProps } from './SelectControl';
import SelectControl from './SelectControl';
import type { TextareaControlProps } from './TextareaControl';
import TextareaControl from './TextareaControl';
import type { TextualControlProps } from './TextualControl';
import TextualControl from './TextualControl';
import type { ToggleControlProps } from './ToggleControl';
import ToggleControl from './ToggleControl';

type ExcludeString<T> = T extends string ? (string extends T ? never : T) : T;

type ControlProps<
  Option extends OptionTypeBase = Record<string, unknown>,
  IsMulti extends boolean = false,
  IsClearable extends boolean = false,
> =
  | ({
      type: 'checkbox';
      toggle: true;
      checked?: boolean;
    } & ToggleControlProps)
  | ({
      type: 'checkbox' | 'radio';
      toggle?: false;
      checked?: boolean;
    } & CheckRadioControlProps)
  | ({
      type?: Exclude<
        ExcludeString<HTMLInputTypeAttribute>,
        'checkbox' | 'radio'
      >;
    } & TextualControlProps)
  | ({
      type: 'textarea';
    } & TextareaControlProps)
  | ({
      type: 'select';
    } & SetOptional<
      SelectControlProps<Option, IsMulti, IsClearable>,
      'onChange'
    >);

type Margin = 'none' | 'compact' | 'small' | 'normal';

const getMargin = ({
  margin,
  type,
}: {
  margin?: Margin;
  type: ControlProps['type'];
}) =>
  margin ||
  (type && ['radio', 'checkbox'].includes(type) ? 'compact' : 'normal');

export type FormFieldProps<
  Option extends OptionTypeBase = Record<string, unknown>,
  IsMulti extends boolean = false,
  IsClearable extends boolean = false,
> = {
  className?: string;
  component?: React.ComponentType<FormFieldProps<Option, IsMulti, IsClearable>>;
  helpText?: React.ReactNode;
  error?: string;
  margin?: Margin;
  label?: React.ReactNode;
  name?: string;
} & ControlProps<Option, IsMulti, IsClearable>;

const FormField = <
  Option extends OptionTypeBase = Record<string, unknown>,
  IsMulti extends boolean = false,
  IsClearable extends boolean = false,
>({
  className,
  component,
  helpText,
  error,
  margin,
  ...childProps
}: FormFieldProps<Option, IsMulti, IsClearable>) => {
  const type = childProps.type ?? 'text';
  const marginClass = {
    none: undefined,
    normal: styles.marginNormal,
    small: styles.marginSmall,
    compact: styles.marginCompact,
  }[getMargin({ margin, type })];

  const classes = classNames(
    className,
    styles.formField,
    !component && styles[type],
    marginClass,
  );

  const commonProps = { error, ...childProps };

  let control;
  if (component) {
    control = createElement(component, commonProps);
  } else if (commonProps.type === 'checkbox' && commonProps.toggle) {
    control = (
      <ToggleControl
        {..._.omit(commonProps, ['toggle'])}
        type={commonProps.type}
      />
    );
  } else if (commonProps.type === 'checkbox' || commonProps.type === 'radio') {
    control = <CheckRadioControl {...commonProps} />;
  } else if (commonProps.type === 'select') {
    control = (
      <SelectControl
        {...commonProps}
        onChange={commonProps.onChange ?? (() => ({}))}
      />
    );
  } else if (commonProps.type === 'textarea') {
    control = <TextareaControl {...commonProps} />;
  } else {
    control = <TextualControl {...commonProps} type={type} />;
  }

  return (
    <div className={classes}>
      {control}
      {!!helpText && <div className={styles.helpText}>{helpText}</div>}
    </div>
  );
};

export default FormField;
